"use client";

import Image from "next/image";
import { Suspense, lazy, useEffect, useState } from "react";
import type { Application } from "@splinetool/runtime";
import { HERO_STATS, HERO_TELEMETRY, SECTION_IDS } from "./content";
import { useScrollToSection } from "@/app/hooks/useScrollToSection";
import { ErrorBoundary } from "../shared/error-boundary";
import { isWebGLAvailable } from "@/app/utils/webgl";
import { cn } from "@/app/utils/className";

// The 3D earth is ~1 MB of scene plus the Spline runtime, so it is split out
// and only requested once the page has finished loading and gone idle.
const Spline = lazy(() => import("@splinetool/react-spline"));

export const Hero = () => {
  const scrollToSection = useScrollToSection();
  const [loadSpline, setLoadSpline] = useState(false);
  const [splineReady, setSplineReady] = useState(false);

  useEffect(() => {
    // Without WebGL the scene can only fail, so keep the static earth and skip
    // the runtime download. Reduced-motion visitors keep the still image too.
    if (!isWebGLAvailable()) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let idleId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const schedule = () => {
      if ("requestIdleCallback" in window) {
        idleId = requestIdleCallback(() => setLoadSpline(true), {
          timeout: 2000,
        });
      } else {
        timeoutId = setTimeout(() => setLoadSpline(true), 100);
      }
    };

    if (document.readyState === "complete") schedule();
    else window.addEventListener("load", schedule, { once: true });

    return () => {
      window.removeEventListener("load", schedule);
      if (idleId !== undefined) cancelIdleCallback(idleId);
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, []);

  const onSplineLoad = (spline: Application) => {
    const width = window.innerWidth;
    spline.setZoom(width < 768 ? 1 : width > 1024 ? 1.75 : 1.5);
    // A beat before cross-fading avoids a one-frame flash of an empty canvas.
    setTimeout(() => setSplineReady(true), 100);
  };

  return (
    <section
      id="hero"
      className="relative flex min-h-[min(100vh,940px)] items-end overflow-hidden px-[clamp(16px,4vw,64px)] pt-[clamp(90px,14vh,180px)] pb-[clamp(48px,7vh,88px)]"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 z-0">
        {/* Static earth: first paint, and the permanent fallback when WebGL is
            unavailable or the scene fails to load. */}
        <Image
          src="/images/earth.webp"
          alt=""
          fill
          priority
          sizes="100vw"
          className={cn(
            "object-cover transition-opacity duration-[1200ms] [animation:dc-drift_46s_ease-in-out_infinite]",
            splineReady ? "opacity-0" : "opacity-[.62]"
          )}
          style={{ objectPosition: "58% 46%" }}
        />

        {/* 3D earth. The boundary is required: react-spline re-throws a failed
            scene load during render, which would otherwise take down the whole
            page. On desktop the canvas is offset right so the globe sits where
            the static image places it, clear of the headline. */}
        {loadSpline && (
          <div
            aria-hidden="true"
            className={cn(
              "pointer-events-none absolute inset-0 transition-opacity duration-[1200ms] lg:left-[16%]",
              splineReady ? "opacity-[.8]" : "opacity-0"
            )}
          >
            <ErrorBoundary fallback={null}>
              <Suspense fallback={null}>
                <Spline
                  scene="/scene/earth.splinecode"
                  className="absolute inset-0"
                  onLoad={onSplineLoad}
                />
              </Suspense>
            </ErrorBoundary>
          </div>
        )}

        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(3,12,22,.86)_0%,rgba(3,12,22,.34)_34%,rgba(3,12,22,.82)_74%,#030C16_100%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,#030C16_2%,rgba(3,12,22,.55)_34%,rgba(3,12,22,.1)_68%,rgba(3,12,22,.6)_100%)]" />
        <div
          className="absolute inset-0 opacity-[.16]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(127,232,180,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(127,232,180,.5) 1px, transparent 1px)",
            backgroundSize: "140px 140px",
          }}
        />
        <div className="absolute inset-x-0 top-0 h-px bg-[linear-gradient(90deg,transparent,rgba(127,232,180,.75),transparent)] [animation:dc-scan_13s_linear_infinite]" />
      </div>

      {/* Telemetry readout */}
      <div className="dc-mono absolute right-[clamp(16px,4vw,64px)] top-[clamp(84px,11vh,132px)] z-2 hidden max-w-[44%] text-right text-[12px] leading-[2.4] tracking-[.2em] text-paper/70 sm:block">
        {HERO_TELEMETRY.map((line) => (
          <div key={line}>{line}</div>
        ))}
        <div className="flex items-center justify-end gap-2.5 text-mint">
          <span className="relative inline-flex h-[9px] w-[9px]">
            <span className="absolute inset-0 rounded-full bg-mint [animation:dc-pulse-dot_4s_ease-in-out_infinite]" />
            <span className="absolute inset-0 rounded-full border border-mint/70 [animation:dc-ring_4s_ease-out_infinite]" />
          </span>
          <span>OBSERVATION 1987–2026 · CURRENT</span>
        </div>
      </div>

      {/* Content */}
      <div className="dc-shell relative z-2 w-full">
        <div className="mb-[30px] flex items-center gap-3.5">
          <span className="h-px w-[54px] bg-mint" />
          <span className="dc-mono text-[12px] uppercase tracking-[.24em] text-mint">
            Spatial data intelligence · Canberra, Australia
          </span>
        </div>

        <h1 className="m-0 text-[clamp(46px,10.5vw,176px)] font-bold leading-[.86] tracking-[-.045em] text-balance">
          EARTH DATA,
          <br />
          LIGHTNING FAST
        </h1>

        <div className="mt-[clamp(36px,5vh,60px)] flex flex-wrap items-end justify-between gap-[clamp(24px,4vw,64px)] border-t border-white/[.16] pt-[30px]">
          <p className="m-0 max-w-[30ch] flex-[1_1_300px] text-[clamp(18px,1.8vw,26px)] leading-[1.45] text-paper-2">
            Turning complex Earth data into answers you can act on.
          </p>
          <div className="flex flex-wrap gap-3.5">
            <a
              href={`#${SECTION_IDS.contact}`}
              onClick={(event) => {
                event.preventDefault();
                scrollToSection(SECTION_IDS.contact);
              }}
              className="bg-mint px-[34px] py-[18px] text-base font-semibold text-ink transition-colors duration-300 hover:bg-paper hover:text-ink"
            >
              Bring us a question
            </a>
            <button
              type="button"
              onClick={() => scrollToSection(SECTION_IDS.capabilities)}
              className="cursor-pointer border border-white/[.34] px-[34px] py-[18px] text-base font-semibold text-paper transition-colors duration-300 hover:border-mint"
            >
              Explore our capabilities
            </button>
          </div>
        </div>

        {/* Credibility strip — carried over from v1 of the design */}
        <div className="mt-[clamp(32px,4vh,52px)] flex flex-wrap gap-x-[clamp(24px,4vw,72px)] gap-y-6 border-t border-white/[.1] pt-[26px]">
          {HERO_STATS.map((stat) => (
            <div key={stat.label} className="min-w-[130px]">
              <div className="text-[clamp(22px,2vw,30px)] font-semibold tracking-[-.02em] text-paper">
                {stat.value}
              </div>
              <div className="dc-mono mt-2 text-[12px] uppercase tracking-[.16em] text-mist-5">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
